import type {
  GithubRun,
  JiraIssue,
  LiveEnv,
  MyFailures,
  PipelineRow,
  PullRequest,
} from './types';

/** Everything the user has on one Jira ticket. */
export interface TicketWork {
  /** `MP-1234`, upper case. */
  key: string;
  /** The Jira ticket when it is assigned to the user in the active sprint. */
  issue?: JiraIssue;
  prs: PullRequest[];
  env?: LiveEnv;
  /** The user's pipelines of the ticket, newest first. */
  pipelines: PipelineRow[];
  failedRuns: GithubRun[];
}

export type AlertKind =
  | 'pipeline-failed'
  | 'run-failed'
  | 'checks-failed'
  | 'changes-requested'
  | 'conflict'
  | 'env-expiring'
  | 'no-branch';

/** Cell of the "À traiter" card an alert is shown in. */
export type AlertGroup = 'ci' | 'jira' | 'env' | 'pr';

export interface Alert {
  kind: AlertKind;
  group: AlertGroup;
  /** `MP-1234`, or null for a PR without a ticket. */
  ticketKey: string | null;
  title: string;
  detail: string;
  /** External URL (PR, GitHub run) or app path. */
  link?: string;
  /** Pipeline to replay from the banner. */
  pipelineId?: string;
}

export interface MyWork {
  /** Tickets assigned to the user in the active sprint, in Jira order. */
  tickets: TicketWork[];
  /** Tickets the user works on (PR, env, pipeline) that are not in their sprint list. */
  otherTickets: TicketWork[];
  /** The user's open PRs whose branch and title carry no Jira key. */
  prsWithoutTicket: PullRequest[];
  /** What is broken or about to be: shown first and counted on the sidebar. */
  alerts: Alert[];
}

const EXPIRY_WARNING_MS = 3600_000;
/** An older pipeline is shown on its ticket's card but does not open a card of its own. */
const RECENT_PIPELINE_MS = 7 * 24 * 3600_000;
const upper = (key: string) => key.toUpperCase();

/**
 * Joins the user's Jira tickets, pull requests, envs, pipelines and failures on the ticket key
 * (`MP-1234` in Jira, `feat/mp-1234` branches, `mp-1234` envs). Pure, so the page only renders.
 */
export function correlateByTicket(input: {
  issues: JiraIssue[];
  /** The user's own open PRs. */
  prs: PullRequest[];
  /** Every live env: the user's are picked here. */
  envs: LiveEnv[];
  /** The user's pipelines, newest first. */
  pipelines: PipelineRow[];
  failures?: MyFailures;
  me: { userRef: string; githubLogin?: string | null };
  now: number;
}): MyWork {
  const { issues, prs, envs, pipelines, failures, me, now } = input;
  const byKey = new Map<string, TicketWork>();
  // A branch or env of a sub-task without a card of its own belongs to its listed parent's card.
  const listed = new Set(issues.map(i => upper(i.key)));
  const parentOf = new Map<string, string>();
  for (const issue of issues)
    for (const sub of issue.subtasks ?? [])
      if (!listed.has(upper(sub.key)))
        parentOf.set(upper(sub.key), upper(issue.key));
  const owner = (key: string) => parentOf.get(upper(key)) ?? upper(key);
  const ticket = (key: string) => {
    const k = owner(key);
    let work = byKey.get(k);
    if (!work) {
      work = { key: k, prs: [], pipelines: [], failedRuns: [] };
      byKey.set(k, work);
    }
    return work;
  };

  for (const issue of issues) ticket(issue.key).issue = issue;

  const prsWithoutTicket: PullRequest[] = [];
  for (const pr of prs) {
    if (pr.ticketKey) ticket(pr.ticketKey).prs.push(pr);
    else prsWithoutTicket.push(pr);
  }

  for (const p of pipelines) {
    const recent =
      input.now - new Date(p.createdAt).getTime() <= RECENT_PIPELINE_MS;
    if (recent || byKey.has(owner(p.ticketKey)))
      ticket(p.ticketKey).pipelines.push(p);
  }
  for (const run of failures?.runs ?? [])
    ticket(run.ticketKey).failedRuns.push(run);

  // The env of a ticket the user works on, whoever launched it, plus the envs they launched.
  const login = me.githubLogin?.toLowerCase();
  for (const env of envs) {
    const mine =
      env.owner.userRef === me.userRef ||
      (login && env.owner.githubLogin?.toLowerCase() === login);
    if (mine || byKey.has(owner(env.ticketKey)))
      ticket(env.ticketKey).env = env;
  }

  const tickets = issues.map(i => byKey.get(upper(i.key))!);
  const assigned = new Set(tickets.map(t => t.key));
  const otherTickets = [...byKey.values()].filter(t => !assigned.has(t.key));

  return {
    tickets,
    otherTickets,
    prsWithoutTicket,
    alerts: alertsOf({ prs, failures, envs: [...byKey.values()], now }),
  };
}

function alertsOf(input: {
  prs: PullRequest[];
  failures?: MyFailures;
  envs: TicketWork[];
  now: number;
}): Alert[] {
  const alerts: Alert[] = [];
  for (const p of input.failures?.pipelines ?? []) {
    alerts.push({
      kind: 'pipeline-failed',
      group: 'env',
      ticketKey: upper(p.ticketKey),
      title: `Pipeline ${
        p.status === 'interrupted' ? 'interrupted' : 'failed'
      }`,
      detail: `${upper(p.ticketKey)} · ${p.branch}`,
      pipelineId: p.id,
    });
  }
  for (const run of input.failures?.runs ?? []) {
    alerts.push({
      kind: 'run-failed',
      group: run.kind === 'e2e' ? 'ci' : 'env',
      ticketKey: upper(run.ticketKey),
      title: `${
        run.kind === 'e2e' ? 'Tests E2E' : 'Déploiement'
      } en échec sur GitHub`,
      detail: `${upper(run.ticketKey)} · ${run.workflowName} · ${run.branch}`,
      link: run.url,
    });
  }
  for (const pr of input.prs) {
    const ticketKey = pr.ticketKey ? upper(pr.ticketKey) : null;
    const detail = `${pr.repo}#${pr.number} · ${pr.title}`;
    if (pr.checks === 'FAILURE' || pr.checks === 'ERROR')
      alerts.push({
        kind: 'checks-failed',
        group: 'ci',
        ticketKey,
        title: 'CI failing',
        detail,
        link: pr.url,
      });
    if (pr.review === 'CHANGES_REQUESTED')
      alerts.push({
        kind: 'changes-requested',
        group: 'pr',
        ticketKey,
        title: 'Modifications demandées',
        detail,
        link: pr.url,
      });
    if (pr.mergeable === 'CONFLICTING')
      alerts.push({
        kind: 'conflict',
        group: 'pr',
        ticketKey,
        title: 'Conflit de merge',
        detail,
        link: pr.url,
      });
  }
  for (const { env } of input.envs) {
    if (!env?.ttlExpiresAt) continue;
    const left = new Date(env.ttlExpiresAt).getTime() - input.now;
    if (left > 0 && left <= EXPIRY_WARNING_MS) {
      alerts.push({
        kind: 'env-expiring',
        group: 'env',
        ticketKey: upper(env.ticketKey),
        title: 'Environnement bientôt expiré',
        detail: `${upper(env.ticketKey)} · reste ${Math.max(
          1,
          Math.round(left / 60_000),
        )} min`,
        link: env.url,
      });
    }
  }
  for (const t of input.envs) {
    // In progress in Jira but no PR nor pipeline: no branch carries the ticket key.
    if (
      t.issue?.status.category === 'indeterminate' &&
      t.prs.length === 0 &&
      t.pipelines.length === 0
    )
      alerts.push({
        kind: 'no-branch',
        group: 'jira',
        ticketKey: t.key,
        title: 'Ticket sans branche liée',
        detail: `${t.key} · ${t.issue.summary}`,
      });
  }
  return alerts;
}
